import { memo, useCallback, useEffect, useState } from 'react';
import { useCanvasStores, useCanvasEntities } from '../stores/CanvasContext';
import { useCanvasLayers } from '../stores/CanvasContext';
import type { CanvasEntity } from '../entityTypes';
import type { ShapeData, TextBlockData, TextLabelData, BlockNodeData } from '../entityTypes';

const IS_LOCALHOST = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b'];

function randomId(): string {
  return `dbg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function randomColor(): string {
  return COLORS[Math.floor(Math.random() * COLORS.length)];
}

type DebugEntityKind = 'shape' | 'textblock' | 'textlabel' | 'blocknode' | 'mix';

const ENTITY_GENERATORS: Record<Exclude<DebugEntityKind, 'mix'>, (layerId: string) => CanvasEntity> = {
  shape: (layerId) => ({
    id: randomId(),
    kind: 'shape',
    x: Math.random() * 20_000 - 5_000,
    y: Math.random() * 20_000 - 5_000,
    width: 80 + Math.random() * 160,
    height: 40 + Math.random() * 100,
    layerId,
    connectable: false,
    zIndex: 0,
    data: {
      shapeType: 'rect',
      backgroundColor: randomColor(),
      cornerRadius: 6,
    } satisfies ShapeData,
  }),

  textblock: (layerId) => ({
    id: randomId(),
    kind: 'textblock',
    x: Math.random() * 20_000 - 5_000,
    y: Math.random() * 20_000 - 5_000,
    width: 160 + Math.random() * 140,
    height: 60 + Math.random() * 80,
    layerId,
    connectable: true,
    zIndex: 0,
    data: {
      text: 'Debug textblock',
      backgroundColor: randomColor(),
      textColor: '#fff',
      fontSize: 14,
      fontWeight: 'normal',
      textAlign: 'left',
    } satisfies TextBlockData,
  }),

  textlabel: (layerId) => ({
    id: randomId(),
    kind: 'textlabel',
    x: Math.random() * 20_000 - 5_000,
    y: Math.random() * 20_000 - 5_000,
    width: 120 + Math.random() * 100,
    height: 30 + Math.random() * 30,
    layerId,
    connectable: false,
    zIndex: 0,
    data: {
      text: 'Label #' + Math.floor(Math.random() * 1000),
      fontSize: 12 + Math.floor(Math.random() * 8),
      fontWeight: Math.random() > 0.5 ? 'bold' : 'normal',
      textColor: '#374151',
    } satisfies TextLabelData,
  }),

  blocknode: (layerId) => ({
    id: randomId(),
    kind: 'blocknode',
    x: Math.random() * 20_000 - 5_000,
    y: Math.random() * 20_000 - 5_000,
    width: 300 + Math.random() * 200,
    height: 150 + Math.random() * 150,
    layerId,
    connectable: true,
    zIndex: 0,
    data: {
      content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Debug block' }] }],
      backgroundColor: '#ffffff',
    } satisfies BlockNodeData,
  }),
};

const MIX_KINDS: Exclude<DebugEntityKind, 'mix'>[] = ['shape', 'textblock', 'textlabel', 'blocknode'];

const BUTTON_STYLE: React.CSSProperties = {
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '3px 8px',
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'monospace',
};

function CanvasDebugToolbarInner() {
  const { store, panZoomStore, visibleEntityIdsRef, debugBatchRef } = useCanvasStores();
  const entities = useCanvasEntities();
  const layers = useCanvasLayers();
  const totalCount = entities.length;

  const [visibleCount, setVisibleCount] = useState(0);
  const [batchInfo, setBatchInfo] = useState({ batchSize: 0, pending: 0 });
  useEffect(() => {
    function sync() {
      setVisibleCount(visibleEntityIdsRef.current.size);
      setBatchInfo({ ...debugBatchRef.current });
    }
    sync();
    const sub1 = store.subscribe(() => requestAnimationFrame(sync));
    const sub2 = panZoomStore.subscribe(() => requestAnimationFrame(sync));
    // Poll frequently so we catch progressive rendering batches
    const interval = setInterval(sync, 50);
    return () => { sub1.unsubscribe(); sub2.unsubscribe(); clearInterval(interval); };
  }, [store, panZoomStore, visibleEntityIdsRef, debugBatchRef]);

  const handleAdd = useCallback((kind: DebugEntityKind, count: number) => {
    const layerId = layers[0]?.id ?? '';
    const batch: CanvasEntity[] = [];

    for (let i = 0; i < count; i++) {
      const k = kind === 'mix'
        ? MIX_KINDS[Math.floor(Math.random() * MIX_KINDS.length)]
        : kind;
      batch.push(ENTITY_GENERATORS[k](layerId));
    }

    store.send({ type: 'entity.batchAdd', entities: batch });
  }, [store, layers]);

  const handleClearDebug = useCallback(() => {
    const ctx = store.getSnapshot().context;
    const nodes = Object.values(ctx.nodes);
    const filtered = nodes.filter(e => !e.id.startsWith('dbg-'));
    if (filtered.length === nodes.length) return;
    const filteredRecord: Record<string, CanvasEntity> = {};
    for (const e of filtered) filteredRecord[e.id] = e;
    store.send({
      type: 'state.restore' as any,
      context: {
        ...ctx,
        nodes: filteredRecord,
      },
    });
  }, [store]);

  if (!IS_LOCALHOST) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(0,0,0,0.75)',
        color: '#fff',
        padding: '6px 12px',
        borderRadius: 8,
        fontSize: 12,
        fontFamily: 'monospace',
        zIndex: 50,
        pointerEvents: 'auto',
        userSelect: 'none',
        flexWrap: 'wrap',
      }}
    >
      <span>
        rendered: <strong>{visibleCount}</strong> / {totalCount}
      </span>
      <span style={{ color: '#6b7280' }}>|</span>
      <span>
        culled: <strong>{totalCount - visibleCount}</strong>
      </span>
      <span style={{ color: '#6b7280' }}>|</span>
      <span>
        batch: <strong>{batchInfo.batchSize}</strong>
      </span>
      <span style={{ color: '#6b7280' }}>|</span>
      <span>
        pending: <strong>{batchInfo.pending}</strong>
      </span>
      <span style={{ color: '#6b7280' }}>|</span>

      <button onClick={() => handleAdd('shape', 10_000)} style={{ ...BUTTON_STYLE, backgroundColor: '#3b82f6' }}>
        +10k shapes
      </button>
      <button onClick={() => handleAdd('textblock', 10_000)} style={{ ...BUTTON_STYLE, backgroundColor: '#8b5cf6' }}>
        +10k textblocks
      </button>
      <button onClick={() => handleAdd('textlabel', 10_000)} style={{ ...BUTTON_STYLE, backgroundColor: '#14b8a6' }}>
        +10k labels
      </button>
      <button onClick={() => handleAdd('blocknode', 1_000)} style={{ ...BUTTON_STYLE, backgroundColor: '#f97316' }}>
        +1k blocknodes
      </button>
      <button onClick={() => handleAdd('mix', 10_000)} style={{ ...BUTTON_STYLE, backgroundColor: '#ec4899' }}>
        +10k mix
      </button>
      <button onClick={handleClearDebug} style={{ ...BUTTON_STYLE, backgroundColor: '#dc2626' }}>
        clear all
      </button>
    </div>
  );
}

export const CanvasDebugToolbar = memo(CanvasDebugToolbarInner);
